import { supabase } from './supabase';
import { breadcrumb, logException } from './logger';
import { ingestSourceRecords, type SourceRecord, type SourceResourceType } from './source-ingestion';

const SHOPIFY_API_VERSION = '2025-10';
const SHOPIFY_PAGE_LIMIT = 250;
const SHOPIFY_GRAPHQL_PRODUCT_PAGE_LIMIT = 75;
const SHOPIFY_GRAPHQL_VARIANT_PAGE_LIMIT = 250;
const SHOPIFY_GRAPHQL_IMAGE_PAGE_LIMIT = 250;
const SHOPIFY_MAX_RETRIES = 4;
const SUPABASE_MAX_RETRIES = 3;
const SOURCE_INGEST_BATCH_SIZE = 25;
const SHOPIFY_PROXY_PATH = '/api/shopify';

type SyncStatus = 'idle' | 'running' | 'success' | 'failed' | 'cancelled';
export type BrowserSyncScope =
  | 'full'
  | 'products'
  | 'recent_products'
  | 'orders'
  | 'customers'
  | 'locations'
  | 'inventory';

const BROWSER_SYNC_SCOPE_LABELS: Record<BrowserSyncScope, string> = {
  full: 'full sync',
  products: 'products sync',
  recent_products: 'recent products sync',
  orders: 'orders sync',
  customers: 'customers sync',
  locations: 'locations sync',
  inventory: 'inventory sync',
};

const BROWSER_SYNC_SCOPE_RESOURCES: Record<BrowserSyncScope, Set<string>> = {
  full: new Set(['products', 'orders', 'customers', 'locations', 'inventory']),
  products: new Set(['products']),
  recent_products: new Set(['products']),
  orders: new Set(['orders']),
  customers: new Set(['customers']),
  locations: new Set(['locations']),
  inventory: new Set(['locations', 'inventory']),
};

interface ActiveConnection {
  shop_domain: string;
  access_token: string;
  sync_progress: Partial<BrowserSyncProgress> | null;
}

interface RetryState {
  attempt: number;
  maxAttempts: number;
  operation: string;
  waitMs: number;
}

interface BrowserSyncCheckpoint {
  nextPageUrl: string | null;
  currentPage: number;
  fetched: number;
  done: boolean;
  updatedAt: string;
}

type BrowserSyncCheckpointMap = Record<string, BrowserSyncCheckpoint>;
type ResumablePhase = 'products' | 'orders' | 'customers' | 'inventory';

const RESUMABLE_PHASES: ResumablePhase[] = ['products', 'orders', 'customers', 'inventory'];

export interface BrowserSyncProgress {
  phase: string;
  resource: string;
  status: SyncStatus;
  fetched: number;
  total: number | null;
  startedAt: string;
  currentPage: number;
  currentLocationId: number | null;
  message: string;
  counts: Record<string, number>;
  errors: string[];
  retrying: RetryState | null;
  completedPhases: ResumablePhase[];
  checkpoints: BrowserSyncCheckpointMap;
}

interface BrowserSyncResult {
  status: SyncStatus;
  counts: Record<string, number>;
  errors: string[];
}

interface ProductVariantSnapshot {
  shopify_product_id: number;
  shopify_variant_ids: number[];
  /**
   * The product's Shopify `updated_at`. Reconciliation reads absence as
   * deletion, so it uses this to leave variants Shopify created after the
   * snapshot alone — a full sync of the catalog takes minutes.
   */
  observed_at: string | null;
}

interface ReconcileProductVariantsResult {
  success: boolean;
  retired: number;
  transferred: number;
  needs_review: number;
}

interface ReconcileProductVariantsCounts {
  retired: number;
  transferred: number;
  needsReview: number;
}

interface BrowserSyncOptions {
  signal?: AbortSignal;
  scope?: BrowserSyncScope;
  onProgress?: (progress: BrowserSyncProgress) => void | Promise<void>;
}

interface ShopifyProduct {
  id: number;
  title: string;
  handle: string;
  body_html: string | null;
  vendor: string | null;
  product_type: string | null;
  status: string;
  tags: string;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  variants?: ShopifyVariant[];
  images?: ShopifyImage[];
}

interface ShopifyVariant {
  id: number;
  product_id: number;
  title: string;
  sku: string | null;
  barcode: string | null;
  price: string;
  compare_at_price: string | null;
  position: number;
  taxable: boolean;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  inventory_quantity: number;
  inventory_item_id: number;
  inventory_management?: string | null;
  requires_shipping: boolean;
  created_at: string;
  updated_at: string;
}

interface ShopifyImage {
  id: number;
  product_id: number;
  src: string;
  alt: string | null;
  width: number;
  height: number;
  position: number;
  variant_ids: number[];
  created_at: string;
  updated_at: string;
}

interface ShopifyGraphqlPageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

interface ShopifyGraphqlEdge<T> {
  cursor: string;
  node: T;
}

interface ShopifyGraphqlConnection<T> {
  pageInfo: ShopifyGraphqlPageInfo;
  edges: ShopifyGraphqlEdge<T>[];
}

interface ShopifyGraphqlProductResponse {
  products: ShopifyGraphqlConnection<ShopifyGraphqlProductNode>;
}

interface ShopifyGraphqlProductNode {
  id: string;
  legacyResourceId: string;
  title: string;
  handle: string;
  descriptionHtml: string | null;
  vendor: string | null;
  productType: string | null;
  status: string;
  tags: string[];
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  variants: ShopifyGraphqlConnection<ShopifyGraphqlVariantNode>;
  images: ShopifyGraphqlConnection<ShopifyGraphqlImageNode>;
}

interface ShopifyGraphqlVariantNode {
  id: string;
  legacyResourceId: string;
  title: string;
  sku: string | null;
  barcode: string | null;
  price: string;
  compareAtPrice: string | null;
  position: number;
  taxable: boolean;
  inventoryQuantity: number;
  inventoryItem: {
    id: string;
    legacyResourceId: string;
    tracked: boolean;
    requiresShipping: boolean;
    createdAt: string;
    updatedAt: string;
  };
  selectedOptions: Array<{ name: string; value: string }>;
  createdAt: string;
  updatedAt: string;
}

interface ShopifyGraphqlImageNode {
  id: string;
  url: string;
  altText: string | null;
  width: number | null;
  height: number | null;
}

interface ShopifyOrder {
  id: number;
  order_number: number;
  name: string;
  total_price: string;
  subtotal_price: string;
  total_tax: string;
  total_discounts: string;
  currency: string;
  financial_status: string | null;
  fulfillment_status: string | null;
  cancel_reason: string | null;
  cancelled_at: string | null;
  customer?: { id: number; email?: string } | null;
  email: string | null;
  shipping_address: Record<string, unknown> | null;
  billing_address: Record<string, unknown> | null;
  note: string | null;
  tags: string;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
  line_items?: ShopifyLineItem[];
}

interface ShopifyLineItem {
  id: number;
  product_id: number | null;
  variant_id: number | null;
  title: string;
  variant_title: string | null;
  sku: string | null;
  quantity: number;
  price: string;
  total_discount: string;
  fulfillment_status: string | null;
}

interface ShopifyCustomer {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  orders_count: number;
  total_spent: string;
  currency: string;
  state: string | null;
  tags: string;
  default_address: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface ShopifyLocation {
  id: number;
  name: string;
  address1: string | null;
  address2: string | null;
  city: string | null;
  province: string | null;
  zip: string | null;
  country: string | null;
  phone: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

interface ShopifyInventoryLevel {
  inventory_item_id: number;
  location_id: number;
  available: number;
  updated_at: string;
}

class BrowserSyncCancelledError extends Error {
  constructor() {
    super('Sync cancelled');
    this.name = 'BrowserSyncCancelledError';
  }
}

class BrowserSyncWriteBlockedError extends Error {
  constructor(operation: string) {
    super(`${operation} failed because the browser session could not write to Supabase. Apply the authenticated write policies before running browser sync.`);
    this.name = 'BrowserSyncWriteBlockedError';
  }
}

function parseTags(tags: string): string[] {
  if (!tags) return [];
  return tags.split(',').map((tag) => tag.trim()).filter(Boolean);
}

function buildShopifyAdminUrl(
  shopDomain: string,
  path: string,
  params?: Record<string, string>,
): string {
  const base = shopDomain.startsWith('http') ? shopDomain : `https://${shopDomain}`;
  const url = new URL(`${base.replace(/\/+$/, '')}/admin/api/${SHOPIFY_API_VERSION}/${path}`);
  for (const [key, value] of Object.entries(params ?? {})) {
    url.searchParams.set(key, value);
  }
  return url.toString();
}

function buildShopifyProxyUrl(targetUrl: string): string {
  const proxyUrl = new URL(SHOPIFY_PROXY_PATH, window.location.origin);
  proxyUrl.searchParams.set('target', targetUrl);
  return proxyUrl.toString();
}

function getBrowserSyncScopeLabel(scope: BrowserSyncScope): string {
  return BROWSER_SYNC_SCOPE_LABELS[scope];
}

function shouldRunSyncResource(scope: BrowserSyncScope, resource: string): boolean {
  return BROWSER_SYNC_SCOPE_RESOURCES[scope].has(resource);
}

function getRecentProductsQuery(days = 30): string {
  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return `updated_at:>=${since.toISOString().slice(0, 10)}`;
}

function parseShopifyLegacyId(value: string | number | null | undefined): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  const raw = String(value ?? '');
  const id = Number(raw.split('/').pop());
  if (!Number.isFinite(id)) {
    throw new Error(`Invalid Shopify ID: ${raw || 'empty'}`);
  }

  return id;
}

function mapGraphqlProductNodeToShopifyProduct(node: ShopifyGraphqlProductNode): ShopifyProduct {
  const productId = parseShopifyLegacyId(node.legacyResourceId || node.id);

  return {
    id: productId,
    title: node.title,
    handle: node.handle,
    body_html: node.descriptionHtml,
    vendor: node.vendor,
    product_type: node.productType,
    status: node.status.toLowerCase(),
    tags: node.tags.join(', '),
    published_at: node.publishedAt,
    created_at: node.createdAt,
    updated_at: node.updatedAt,
    variants: node.variants.edges.map(({ node: variant }) => {
      const inventoryItem = variant.inventoryItem;
      const selectedOptions = variant.selectedOptions ?? [];

      return {
        id: parseShopifyLegacyId(variant.legacyResourceId || variant.id),
        product_id: productId,
        title: variant.title,
        sku: variant.sku,
        barcode: variant.barcode || null,
        price: variant.price,
        compare_at_price: variant.compareAtPrice,
        position: variant.position,
        taxable: variant.taxable,
        option1: selectedOptions[0]?.value ?? null,
        option2: selectedOptions[1]?.value ?? null,
        option3: selectedOptions[2]?.value ?? null,
        inventory_quantity: variant.inventoryQuantity ?? 0,
        inventory_item_id: parseShopifyLegacyId(inventoryItem.legacyResourceId || inventoryItem.id),
        inventory_management: inventoryItem.tracked ? 'shopify' : null,
        requires_shipping: inventoryItem.requiresShipping,
        created_at: variant.createdAt,
        updated_at: variant.updatedAt,
      };
    }),
    images: node.images.edges.map(({ node: image }, index) => ({
      id: parseShopifyLegacyId(image.id),
      product_id: productId,
      src: image.url,
      alt: image.altText,
      width: image.width ?? 0,
      height: image.height ?? 0,
      position: index + 1,
      variant_ids: [],
      created_at: node.createdAt,
      updated_at: node.updatedAt,
    })),
  };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function isBrowserSyncCancelled(error: unknown): boolean {
  return error instanceof BrowserSyncCancelledError || isAbortError(error);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isResumablePhase(value: string): value is ResumablePhase {
  return RESUMABLE_PHASES.includes(value as ResumablePhase);
}

function shouldRetryShopify(error: unknown): boolean {
  if (isBrowserSyncCancelled(error)) return false;
  if (error instanceof Error && 'status' in error) {
    const status = Number((error as Error & { status?: number }).status);
    return status === 408 || status === 429 || status >= 500;
  }
  return true;
}

function shouldRetrySupabase(error: unknown): boolean {
  if (isBrowserSyncCancelled(error)) return false;
  if (!(error instanceof Error)) return false;

  const code = typeof (error as Error & { code?: unknown }).code === 'string'
    ? (error as Error & { code: string }).code
    : null;
  const message = error.message.toLowerCase();
  return (
    code === '57014' ||
    message.includes('network') ||
    message.includes('fetch') ||
    message.includes('timeout') ||
    message.includes('statement timeout') ||
    message.includes('failed to fetch')
  );
}

function assertNotAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new BrowserSyncCancelledError();
  }
}

async function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  if (ms <= 0) return;

  await new Promise<void>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      window.clearTimeout(timeoutId);
      reject(new BrowserSyncCancelledError());
    };

    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

async function withRetry<T>(
  operation: string,
  task: () => Promise<T>,
  options: {
    signal?: AbortSignal;
    maxAttempts: number;
    shouldRetry: (error: unknown) => boolean;
    onRetry?: (retrying: RetryState | null) => Promise<void>;
  },
): Promise<T> {
  const { signal, maxAttempts, shouldRetry, onRetry } = options;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    assertNotAborted(signal);

    try {
      const result = await task();
      await onRetry?.(null);
      return result;
    } catch (error) {
      if (attempt >= maxAttempts || !shouldRetry(error)) {
        await onRetry?.(null);
        throw error;
      }

      const waitMs = 1000 * 2 ** (attempt - 1);
      await onRetry?.({ attempt, maxAttempts, operation, waitMs });
      await sleep(waitMs, signal);
    }
  }

  throw new Error(`${operation} failed after ${maxAttempts} attempts`);
}

async function loadActiveConnection(signal?: AbortSignal): Promise<ActiveConnection> {
  assertNotAborted(signal);

  const { data, error } = await supabase
    .from('shopify_connections')
    .select('shop_domain, access_token, sync_progress')
    .eq('is_active', true)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data?.shop_domain || !data?.access_token) {
    throw new Error('No active Shopify connection found');
  }

  return data;
}

async function updateConnectionProgress(progress: BrowserSyncProgress, signal?: AbortSignal): Promise<void> {
  assertNotAborted(signal);

  const { data, error } = await supabase
    .from('shopify_connections')
    .update({
      sync_progress: {
        phase: progress.phase,
        fetched: progress.fetched,
        total: progress.total,
        startedAt: progress.startedAt,
        status: progress.status,
        resource: progress.resource,
        currentPage: progress.currentPage,
        currentLocationId: progress.currentLocationId,
        message: progress.message,
        counts: progress.counts,
        errors: progress.errors.slice(-10),
        retrying: progress.retrying,
        completedPhases: progress.completedPhases,
        checkpoints: progress.checkpoints,
      },
    })
    .eq('is_active', true)
    .select('id');

  if (error) {
    throw error;
  }
  if (!data?.length) {
    throw new BrowserSyncWriteBlockedError('Updating sync progress');
  }
}

async function clearConnectionProgress(lastFullSyncAt: string | null, signal?: AbortSignal): Promise<void> {
  assertNotAborted(signal);

  const payload: { sync_progress: null; last_full_sync_at?: string } = { sync_progress: null };
  if (lastFullSyncAt) {
    payload.last_full_sync_at = lastFullSyncAt;
  }

  const { data, error } = await supabase
    .from('shopify_connections')
    .update(payload)
    .eq('is_active', true)
    .select('id');

  if (error) {
    throw error;
  }
  if (!data?.length) {
    throw new BrowserSyncWriteBlockedError('Clearing sync progress');
  }
}

function normalizeNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function normalizeString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

function normalizeCounts(value: unknown): Record<string, number> {
  if (!isRecord(value)) return {};

  return Object.fromEntries(
    Object.entries(value)
      .filter((entry): entry is [string, number] => typeof entry[1] === 'number' && Number.isFinite(entry[1])),
  );
}

function normalizeErrors(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((error): error is string => typeof error === 'string') : [];
}

function normalizeCompletedPhases(value: unknown, currentPhase: string): ResumablePhase[] {
  const phases = new Set<ResumablePhase>();
  const inferredCompletedPhases: Record<string, ResumablePhase[]> = {
    products: [],
    orders: ['products'],
    customers: ['products', 'orders'],
    locations: ['products', 'orders', 'customers'],
    inventory: ['products', 'orders', 'customers'],
    finalizing: ['products', 'orders', 'customers', 'inventory'],
    complete: ['products', 'orders', 'customers', 'inventory'],
  };

  if (Array.isArray(value)) {
    for (const phase of value) {
      if (typeof phase === 'string' && isResumablePhase(phase)) {
        phases.add(phase);
      }
    }
  }

  for (const phase of inferredCompletedPhases[currentPhase] ?? []) {
    phases.add(phase);
  }

  return [...phases];
}

function normalizeCheckpoint(value: unknown): BrowserSyncCheckpoint | null {
  if (!isRecord(value)) return null;

  const nextPageUrl = value.nextPageUrl;
  return {
    nextPageUrl: typeof nextPageUrl === 'string' ? nextPageUrl : null,
    currentPage: normalizeNumber(value.currentPage, 0),
    fetched: normalizeNumber(value.fetched, 0),
    done: value.done === true,
    updatedAt: normalizeString(value.updatedAt, nowIso()),
  };
}

function normalizeCheckpoints(value: unknown): BrowserSyncCheckpointMap {
  if (!isRecord(value)) return {};

  const checkpoints: BrowserSyncCheckpointMap = {};
  for (const [key, checkpoint] of Object.entries(value)) {
    const normalized = normalizeCheckpoint(checkpoint);
    if (normalized) {
      checkpoints[key] = normalized;
    }
  }
  return checkpoints;
}

function restoreProgress(savedProgress: unknown): BrowserSyncProgress | null {
  if (!isRecord(savedProgress) || typeof savedProgress.phase !== 'string') {
    return null;
  }

  const progress = createProgress();
  progress.phase = savedProgress.phase;
  progress.resource = normalizeString(savedProgress.resource, progress.resource);
  progress.status = 'running';
  progress.fetched = normalizeNumber(savedProgress.fetched, progress.fetched);
  progress.total =
    typeof savedProgress.total === 'number' || savedProgress.total === null
      ? savedProgress.total
      : progress.total;
  progress.startedAt = normalizeString(savedProgress.startedAt, progress.startedAt);
  progress.currentPage = normalizeNumber(savedProgress.currentPage, progress.currentPage);
  progress.currentLocationId =
    typeof savedProgress.currentLocationId === 'number' || savedProgress.currentLocationId === null
      ? savedProgress.currentLocationId
      : progress.currentLocationId;
  progress.message = `Resuming ${savedProgress.phase} sync...`;
  progress.counts = normalizeCounts(savedProgress.counts);
  progress.errors = normalizeErrors(savedProgress.errors);
  progress.retrying = null;
  progress.completedPhases = normalizeCompletedPhases(savedProgress.completedPhases, savedProgress.phase);
  progress.checkpoints = normalizeCheckpoints(savedProgress.checkpoints);
  return progress;
}

function getCheckpoint(progress: BrowserSyncProgress, key: string): BrowserSyncCheckpoint | null {
  return progress.checkpoints[key] ?? null;
}

function saveCheckpoint(
  progress: BrowserSyncProgress,
  key: string,
  checkpoint: Omit<BrowserSyncCheckpoint, 'updatedAt'>,
): void {
  progress.checkpoints[key] = {
    ...checkpoint,
    updatedAt: nowIso(),
  };
}

function isPhaseComplete(progress: BrowserSyncProgress, phase: ResumablePhase): boolean {
  return progress.completedPhases.includes(phase) || progress.checkpoints[phase]?.done === true;
}

function markPhaseComplete(progress: BrowserSyncProgress, phase: ResumablePhase): void {
  if (!progress.completedPhases.includes(phase)) {
    progress.completedPhases = [...progress.completedPhases, phase];
  }
}

function getPaginationResumeOptions(
  progress: BrowserSyncProgress,
  checkpointKey: string,
): { initialUrl?: string; initialPage?: number } {
  const checkpoint = getCheckpoint(progress, checkpointKey);
  if (!checkpoint || checkpoint.done || !checkpoint.nextPageUrl) {
    return {};
  }

  return {
    initialUrl: checkpoint.nextPageUrl,
    initialPage: checkpoint.currentPage,
  };
}

async function insertSyncEvent(event: Record<string, unknown>, signal?: AbortSignal): Promise<void> {
  assertNotAborted(signal);

  const { error } = await supabase.from('sync_events').insert(event);
  if (error) {
    throw error;
  }
}

async function refreshDashboardStats(signal?: AbortSignal): Promise<void> {
  assertNotAborted(signal);

  const { error } = await supabase.rpc('refresh_dashboard_stats');
  if (error) {
    throw error;
  }
}

async function shopifyRequestJson<T>(
  url: string,
  accessToken: string,
  signal?: AbortSignal,
): Promise<{ data: T; nextPageUrl: string | null }> {
  assertNotAborted(signal);

  // The sync orchestration stays in the browser on purpose because Supabase free tier
  // cannot reliably hold the full catalog sync open. We only proxy the Shopify Admin
  // transport through our own origin so the browser can keep paginating and writing
  // directly to Supabase without tripping cross-origin fetch restrictions.
  const response = await fetch(buildShopifyProxyUrl(url), {
    method: 'GET',
    signal,
    headers: {
      'X-Shopify-Access-Token': accessToken,
      Accept: 'application/json',
    },
  });

  const rawBody = await response.text();

  if (!response.ok) {
    const detail = rawBody.trim();
    const error = new Error(
      detail ? `Shopify request failed (${response.status}): ${detail}` : `Shopify request failed (${response.status})`,
    );
    (error as Error & { status?: number; body?: string }).status = response.status;
    (error as Error & { status?: number; body?: string }).body = detail;
    throw error;
  }

  const payload = JSON.parse(rawBody) as { data: T; nextPageUrl?: string | null };
  return {
    data: payload.data,
    nextPageUrl: payload.nextPageUrl ?? null,
  };
}

const SHOPIFY_PRODUCTS_GRAPHQL_QUERY = `
  query ProductsForSync(
    $first: Int!
    $after: String
    $query: String
    $variantFirst: Int!
    $imageFirst: Int!
  ) {
    products(first: $first, after: $after, query: $query) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          legacyResourceId
          title
          handle
          descriptionHtml
          vendor
          productType
          status
          tags
          publishedAt
          createdAt
          updatedAt
          variants(first: $variantFirst) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              cursor
              node {
                id
                legacyResourceId
                title
                sku
                barcode
                price
                compareAtPrice
                position
                taxable
                inventoryQuantity
                inventoryItem {
                  id
                  legacyResourceId
                  tracked
                  requiresShipping
                  createdAt
                  updatedAt
                }
                selectedOptions {
                  name
                  value
                }
                createdAt
                updatedAt
              }
            }
          }
          images(first: $imageFirst) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              cursor
              node {
                id
                url
                altText
                width
                height
              }
            }
          }
        }
      }
    }
  }
`;

async function shopifyGraphqlRequest<T>(
  connection: ActiveConnection,
  query: string,
  variables: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  assertNotAborted(signal);

  const response = await fetch(buildShopifyProxyUrl(buildShopifyAdminUrl(connection.shop_domain, 'graphql.json')), {
    method: 'POST',
    signal,
    headers: {
      'X-Shopify-Access-Token': connection.access_token,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ query, variables }),
  });

  const rawBody = await response.text();
  if (!response.ok) {
    const detail = rawBody.trim();
    const error = new Error(
      detail ? `Shopify GraphQL request failed (${response.status}): ${detail}` : `Shopify GraphQL request failed (${response.status})`,
    );
    (error as Error & { status?: number; body?: string }).status = response.status;
    (error as Error & { status?: number; body?: string }).body = detail;
    throw error;
  }

  const proxyPayload = JSON.parse(rawBody) as {
    data?: { data?: T; errors?: Array<{ message?: string }> };
  };
  const graphqlPayload = proxyPayload.data;
  if (!graphqlPayload) {
    throw new Error('Shopify GraphQL request failed: missing response payload');
  }
  if (graphqlPayload.errors?.length) {
    const message = graphqlPayload.errors.map((item) => item.message || 'Unknown GraphQL error').join('; ');
    throw new Error(`Shopify GraphQL request failed: ${message}`);
  }
  if (!graphqlPayload.data) {
    throw new Error('Shopify GraphQL request failed: missing data');
  }

  return graphqlPayload.data;
}

function getFirstArrayValue<T>(payload: Record<string, unknown>): T[] {
  const resource = Object.values(payload).find((value) => Array.isArray(value));
  return Array.isArray(resource) ? (resource as T[]) : [];
}

async function paginateShopifyResource<T>(
  connection: ActiveConnection,
  path: string,
  params: Record<string, string> | undefined,
  onPage: (items: T[], page: number, nextPageUrl: string | null) => Promise<void>,
  retryState: (retrying: RetryState | null) => Promise<void>,
  signal?: AbortSignal,
  options: { initialUrl?: string | null; initialPage?: number } = {},
): Promise<void> {
  let nextUrl: string | null = options.initialUrl ?? buildShopifyAdminUrl(connection.shop_domain, path, {
    limit: String(SHOPIFY_PAGE_LIMIT),
    ...(params ?? {}),
  });
  let page = options.initialPage ?? 0;

  while (nextUrl) {
    assertNotAborted(signal);
    page += 1;

    const { data, nextPageUrl } = await withRetry(
      `shopify:${path}:page:${page}`,
      () => shopifyRequestJson<Record<string, unknown>>(nextUrl!, connection.access_token, signal),
      {
        signal,
        maxAttempts: SHOPIFY_MAX_RETRIES,
        shouldRetry: shouldRetryShopify,
        onRetry: retryState,
      },
    );

    await onPage(getFirstArrayValue<T>(data), page, nextPageUrl);
    nextUrl = nextPageUrl;
  }
}

async function paginateShopifyProductsGraphql(
  connection: ActiveConnection,
  options: {
    after?: string | null;
    initialPage?: number;
    query?: string | null;
  },
  onPage: (items: ShopifyProduct[], page: number, nextCursor: string | null) => Promise<void>,
  retryState: (retrying: RetryState | null) => Promise<void>,
  signal?: AbortSignal,
): Promise<void> {
  let cursor = options.after ?? null;
  let page = options.initialPage ?? 0;

  while (true) {
    assertNotAborted(signal);
    page += 1;

    const data = await withRetry(
      `shopify:graphql:products:page:${page}`,
      () => shopifyGraphqlRequest<ShopifyGraphqlProductResponse>(
        connection,
        SHOPIFY_PRODUCTS_GRAPHQL_QUERY,
        {
          first: SHOPIFY_GRAPHQL_PRODUCT_PAGE_LIMIT,
          after: cursor,
          query: options.query ?? null,
          variantFirst: SHOPIFY_GRAPHQL_VARIANT_PAGE_LIMIT,
          imageFirst: SHOPIFY_GRAPHQL_IMAGE_PAGE_LIMIT,
        },
        signal,
      ),
      {
        signal,
        maxAttempts: SHOPIFY_MAX_RETRIES,
        shouldRetry: shouldRetryShopify,
        onRetry: retryState,
      },
    );

    const products = data.products.edges.map(({ node }) => {
      if (node.variants.pageInfo.hasNextPage || node.images.pageInfo.hasNextPage) {
        throw new Error(`Shopify product ${node.legacyResourceId} has more variants/images than the sync query currently fetches`);
      }
      return mapGraphqlProductNodeToShopifyProduct(node);
    });
    const nextCursor = data.products.pageInfo.hasNextPage ? data.products.pageInfo.endCursor : null;

    await onPage(products, page, nextCursor);

    if (!nextCursor) {
      break;
    }
    cursor = nextCursor;
  }
}

async function ingestRecords(
  records: SourceRecord[],
  retryState: (retrying: RetryState | null) => Promise<void>,
  signal?: AbortSignal,
  context: Record<string, unknown> = {},
): Promise<void> {
  if (records.length === 0) return;

  for (let index = 0; index < records.length; index += SOURCE_INGEST_BATCH_SIZE) {
    const batch = records.slice(index, index + SOURCE_INGEST_BATCH_SIZE);

    await withRetry(
      `supabase:source_ingestion:batch:${index / SOURCE_INGEST_BATCH_SIZE + 1}`,
      async () => {
        assertNotAborted(signal);
        await ingestSourceRecords('shopify', batch, {
          browser: true,
          sync: 'full_sync',
          ...context,
        });
      },
      {
        signal,
        maxAttempts: SUPABASE_MAX_RETRIES,
        shouldRetry: shouldRetrySupabase,
        onRetry: retryState,
      },
    );
  }
}

async function reconcileProductVariants(
  snapshots: ProductVariantSnapshot[],
  retryState: (retrying: RetryState | null) => Promise<void>,
  signal?: AbortSignal,
): Promise<ReconcileProductVariantsCounts> {
  if (snapshots.length === 0) {
    return { retired: 0, transferred: 0, needsReview: 0 };
  }

  return withRetry(
    'supabase:variant_reconciliation',
    async () => {
      assertNotAborted(signal);
      const { data, error } = await supabase.functions.invoke<ReconcileProductVariantsResult>(
        'reconcile-shopify-variants',
        { body: { snapshots } },
      );

      if (error) {
        let message = error.message;
        try {
          const detail = await error.context.json();
          if (isRecord(detail) && typeof detail.error === 'string') {
            message = detail.error;
          }
        } catch {
          // Keep the Supabase wrapper message when the response has no JSON body.
        }
        const reconciliationError = new Error(message);
        reconciliationError.name = error.name;
        throw reconciliationError;
      }

      if (
        !data?.success ||
        !Number.isSafeInteger(data.retired) ||
        data.retired < 0 ||
        !Number.isSafeInteger(data.transferred) ||
        data.transferred < 0 ||
        !Number.isSafeInteger(data.needs_review) ||
        data.needs_review < 0
      ) {
        throw new Error('Variant reconciliation returned an invalid response');
      }

      return {
        retired: data.retired,
        transferred: data.transferred,
        needsReview: data.needs_review,
      };
    },
    {
      signal,
      maxAttempts: SUPABASE_MAX_RETRIES,
      shouldRetry: shouldRetrySupabase,
      onRetry: retryState,
    },
  );
}

function toSourceRecord(
  resourceType: SourceResourceType,
  externalId: string | number,
  data: Record<string, unknown>,
  raw: Record<string, unknown>,
  options: {
    operation?: SourceRecord['operation'];
    sourceUpdatedAt?: string | null;
  } = {},
): SourceRecord {
  return {
    resource_type: resourceType,
    external_id: String(externalId),
    operation: options.operation ?? 'upsert',
    data,
    raw,
    source_updated_at:
      options.sourceUpdatedAt ??
      (typeof data.shopify_updated_at === 'string' ? data.shopify_updated_at : null),
  };
}

function createProgress(): BrowserSyncProgress {
  return {
    phase: 'initializing',
    resource: 'system',
    status: 'running',
    fetched: 0,
    total: null,
    startedAt: new Date().toISOString(),
    currentPage: 0,
    currentLocationId: null,
    message: 'Preparing browser sync',
    counts: {},
    errors: [],
    retrying: null,
    completedPhases: [],
    checkpoints: {},
  };
}

function nowIso(): string {
  return new Date().toISOString();
}

function mapShopifyProduct(product: ShopifyProduct) {
  return {
    shopify_product_id: product.id,
    title: product.title,
    handle: product.handle,
    body_html: product.body_html,
    vendor: product.vendor,
    product_type: product.product_type,
    status: product.status,
    tags: parseTags(product.tags),
    published_at: product.published_at,
    shopify_created_at: product.created_at,
    shopify_updated_at: product.updated_at,
    is_deleted: false,
    deleted_at: null,
    raw_data: product as unknown as Record<string, unknown>,
    updated_at: nowIso(),
  };
}

function mapShopifyVariant(variant: ShopifyVariant) {
  return {
    shopify_variant_id: variant.id,
    shopify_product_id: variant.product_id,
    shopify_inventory_item_id: variant.inventory_item_id,
    title: variant.title,
    sku: variant.sku,
    barcode: variant.barcode,
    price: parseFloat(variant.price) || null,
    compare_at_price: variant.compare_at_price ? parseFloat(variant.compare_at_price) : null,
    position: variant.position,
    taxable: variant.taxable,
    option1: variant.option1,
    option2: variant.option2,
    option3: variant.option3,
    inventory_quantity: variant.inventory_quantity,
    requires_shipping: variant.requires_shipping,
    shopify_created_at: variant.created_at,
    shopify_updated_at: variant.updated_at,
    is_deleted: false,
    deleted_at: null,
    raw_data: variant as unknown as Record<string, unknown>,
    updated_at: nowIso(),
  };
}

function mapShopifyImage(image: ShopifyImage) {
  return {
    shopify_image_id: image.id,
    shopify_product_id: image.product_id,
    src: image.src,
    alt_text: image.alt,
    width: image.width,
    height: image.height,
    position: image.position,
    variant_ids: image.variant_ids,
    shopify_created_at: image.created_at,
    shopify_updated_at: image.updated_at,
  };
}

function mapInventoryItemFromVariant(variant: ShopifyVariant) {
  return {
    shopify_inventory_item_id: variant.inventory_item_id,
    shopify_variant_id: variant.id,
    sku: variant.sku,
    tracked: variant.inventory_management === 'shopify',
    requires_shipping: variant.requires_shipping,
    cost: null,
    country_code_of_origin: null,
    shopify_created_at: variant.created_at,
    shopify_updated_at: variant.updated_at,
    updated_at: nowIso(),
  };
}

function mapShopifyOrder(order: ShopifyOrder) {
  return {
    shopify_order_id: order.id,
    order_number: String(order.order_number),
    name: order.name,
    total_price: parseFloat(order.total_price) || null,
    subtotal_price: parseFloat(order.subtotal_price) || null,
    total_tax: parseFloat(order.total_tax) || null,
    total_discounts: parseFloat(order.total_discounts) || null,
    currency: order.currency || 'USD',
    financial_status: order.financial_status,
    fulfillment_status: order.fulfillment_status,
    cancel_reason: order.cancel_reason,
    cancelled_at: order.cancelled_at,
    customer_email: order.email || order.customer?.email || null,
    customer_id: order.customer?.id || null,
    shipping_address: order.shipping_address,
    billing_address: order.billing_address,
    note: order.note,
    tags: parseTags(order.tags),
    processed_at: order.processed_at,
    shopify_created_at: order.created_at,
    shopify_updated_at: order.updated_at,
    raw_data: order as unknown as Record<string, unknown>,
    updated_at: nowIso(),
  };
}

function mapShopifyLineItem(item: ShopifyLineItem, orderId: number) {
  return {
    shopify_line_item_id: item.id,
    shopify_order_id: orderId,
    shopify_product_id: item.product_id,
    shopify_variant_id: item.variant_id,
    title: item.title,
    variant_title: item.variant_title,
    sku: item.sku,
    quantity: item.quantity,
    price: parseFloat(item.price) || null,
    total_discount: parseFloat(item.total_discount) || null,
    fulfillment_status: item.fulfillment_status,
  };
}

function mapShopifyCustomer(customer: ShopifyCustomer) {
  return {
    shopify_customer_id: customer.id,
    first_name: customer.first_name,
    last_name: customer.last_name,
    email: customer.email,
    phone: customer.phone,
    orders_count: customer.orders_count,
    total_spent: parseFloat(customer.total_spent) || 0,
    currency: customer.currency || 'USD',
    state: customer.state,
    tags: parseTags(customer.tags),
    default_address: customer.default_address,
    shopify_created_at: customer.created_at,
    shopify_updated_at: customer.updated_at,
    raw_data: customer as unknown as Record<string, unknown>,
    updated_at: nowIso(),
  };
}

function mapShopifyLocation(location: ShopifyLocation) {
  return {
    shopify_location_id: location.id,
    name: location.name,
    address1: location.address1,
    address2: location.address2,
    city: location.city,
    province: location.province,
    zip: location.zip,
    country: location.country,
    phone: location.phone,
    active: location.active,
    is_primary: false,
    shopify_created_at: location.created_at,
    shopify_updated_at: location.updated_at,
    updated_at: nowIso(),
  };
}

function mapShopifyInventoryLevel(level: ShopifyInventoryLevel) {
  return {
    shopify_inventory_item_id: level.inventory_item_id,
    shopify_location_id: level.location_id,
    available: level.available ?? 0,
    is_connected: true,
    shopify_updated_at: level.updated_at,
    updated_at: nowIso(),
  };
}

export async function runBrowserSync(options: BrowserSyncOptions = {}): Promise<BrowserSyncResult> {
  const { signal, onProgress } = options;
  const scope = options.scope ?? 'full';
  const scopeLabel = getBrowserSyncScopeLabel(scope);
  const persistProgress = scope === 'full';
  const progress = createProgress();
  const errors: string[] = [];

  const publishProgress = async (patch: Partial<BrowserSyncProgress>, persistSignal = signal) => {
    Object.assign(progress, patch);
    progress.errors = [...errors];

    if (persistProgress) {
      await updateConnectionProgress(progress, persistSignal);
    }
    await onProgress?.({ ...progress, counts: { ...progress.counts }, errors: [...progress.errors] });
  };

  const setRetryState = async (retrying: RetryState | null) => {
    await publishProgress({ retrying });
  };

  const logPhase = async (phase: string, status: 'success' | 'failed' | 'skipped', description: string) => {
    await insertSyncEvent(
      {
        event_type: 'sync_phase',
        event_category: 'system',
        direction: 'inbound',
        title: `Browser phase "${phase}" ${status}`,
        description,
        status,
        error_message: status === 'failed' ? errors.slice(-5).join('; ') : null,
        source: scope === 'full' ? 'full_sync' : scope,
        metadata: {
          browser: true,
          scope,
          phase,
          counts: progress.counts,
          errors: progress.errors,
        },
      },
      signal,
    );
  };

  const recordError = (message: string, error?: unknown) => {
    errors.push(message);
    if (error) {
      logException(error, { operation: 'browser_sync', message });
    }
  };

  try {
    breadcrumb(`Starting browser-side Shopify ${scopeLabel}`);
    const connection = await withRetry('load active Shopify connection', () => loadActiveConnection(signal), {
      signal,
      maxAttempts: SUPABASE_MAX_RETRIES,
      shouldRetry: shouldRetrySupabase,
      onRetry: setRetryState,
    });

    const savedProgress = persistProgress ? restoreProgress(connection.sync_progress) : null;
    if (savedProgress) {
      Object.assign(progress, savedProgress);
      errors.splice(0, errors.length, ...savedProgress.errors);
      if (progress.completedPhases.includes('products') && (progress.counts.products ?? 0) === 0) {
        progress.completedPhases = progress.completedPhases.filter((phase) => phase !== 'products');
        delete progress.checkpoints.products;
      }
      await publishProgress({
        status: 'running',
        retrying: null,
        message: `Resuming ${savedProgress.phase} sync...`,
      });
    }

    if (shouldRunSyncResource(scope, 'products') && (!persistProgress || !isPhaseComplete(progress, 'products'))) {
      const productsCheckpoint = getCheckpoint(progress, 'products');
      const productCursor = productsCheckpoint?.done ? null : productsCheckpoint?.nextPageUrl ?? null;
      const productInitialPage = productsCheckpoint?.done ? 0 : productsCheckpoint?.currentPage ?? 0;
      const productQuery = scope === 'recent_products' ? getRecentProductsQuery() : null;

      if (!productCursor) {
        progress.counts.products = 0;
        progress.counts.variants = 0;
        progress.counts.images = 0;
        progress.counts.inventoryItems = 0;
      }

      await publishProgress({
        phase: 'products',
        resource: 'products',
        fetched: progress.counts.products ?? 0,
        currentPage: productInitialPage,
        message: productCursor
          ? `Resuming products from page ${productInitialPage + 1}...`
          : `Connected to ${connection.shop_domain}. Fetching ${scope === 'recent_products' ? 'recent ' : ''}products...`,
      });

      await paginateShopifyProductsGraphql(
        connection,
        {
          after: productCursor,
          initialPage: productInitialPage,
          query: productQuery,
        },
        async (products, page, nextPageUrl) => {
          const variants = products.flatMap((product) =>
            (product.variants ?? []).map((variant) => ({ ...variant, product_id: product.id })),
          );
          const images = products.flatMap((product) =>
            (product.images ?? []).map((image) => ({ ...image, product_id: product.id })),
          );
          const inventoryItems = variants.map(mapInventoryItemFromVariant);
          const sourceRecords: SourceRecord[] = [
            ...products.map((product) =>
              toSourceRecord('product', product.id, mapShopifyProduct(product), product as unknown as Record<string, unknown>, {
                sourceUpdatedAt: product.updated_at,
              }),
            ),
            ...variants.map((variant) =>
              toSourceRecord('variant', variant.id, mapShopifyVariant(variant), variant as unknown as Record<string, unknown>, {
                sourceUpdatedAt: variant.updated_at,
              }),
            ),
            ...images.map((image) =>
              toSourceRecord('product_image', image.id, mapShopifyImage(image), image as unknown as Record<string, unknown>, {
                sourceUpdatedAt: image.updated_at,
              }),
            ),
            ...inventoryItems.map((item) =>
              toSourceRecord('inventory_item', item.shopify_inventory_item_id, item, item, {
                sourceUpdatedAt: item.shopify_updated_at,
              }),
            ),
          ];

          await ingestRecords(sourceRecords, setRetryState, signal, {
            phase: 'products',
            shop: connection.shop_domain,
          });

          const reconciliation = await reconcileProductVariants(
            products.map((product) => ({
              shopify_product_id: product.id,
              shopify_variant_ids: (product.variants ?? []).map((variant) => variant.id),
              observed_at: product.updated_at ?? null,
            })),
            setRetryState,
            signal,
          );

          progress.counts.products = (progress.counts.products ?? 0) + products.length;
          progress.counts.variants = (progress.counts.variants ?? 0) + variants.length;
          progress.counts.images = (progress.counts.images ?? 0) + images.length;
          progress.counts.inventoryItems = (progress.counts.inventoryItems ?? 0) + inventoryItems.length;
          progress.counts.retiredVariants = (progress.counts.retiredVariants ?? 0) + reconciliation.retired;
          progress.counts.transferredVariantDetails =
            (progress.counts.transferredVariantDetails ?? 0) + reconciliation.transferred;
          progress.counts.variantTransfersNeedingReview =
            (progress.counts.variantTransfersNeedingReview ?? 0) + reconciliation.needsReview;

          saveCheckpoint(progress, 'products', {
            nextPageUrl,
            currentPage: page,
            fetched: progress.counts.products,
            done: nextPageUrl === null,
          });

          await publishProgress({
            phase: 'products',
            resource: 'products',
            fetched: progress.counts.products,
            currentPage: page,
            message: `Imported ${progress.counts.products} products across ${page} page${page === 1 ? '' : 's'}`,
          });
        },
        setRetryState,
        signal,
      );
      markPhaseComplete(progress, 'products');
      await logPhase(
        scope === 'recent_products' ? 'recent_products' : 'products',
        errors.length > 0 ? 'failed' : 'success',
        `Products: ${progress.counts.products ?? 0}, Variants: ${progress.counts.variants ?? 0}, Retired variants: ${progress.counts.retiredVariants ?? 0}, Transferred details: ${progress.counts.transferredVariantDetails ?? 0}, Needs review: ${progress.counts.variantTransfersNeedingReview ?? 0}, Images: ${progress.counts.images ?? 0}`,
      );
    }

    if (shouldRunSyncResource(scope, 'orders') && (!persistProgress || !isPhaseComplete(progress, 'orders'))) {
      const ordersResume = getPaginationResumeOptions(progress, 'orders');
      if (!ordersResume.initialUrl) {
        progress.counts.orders = 0;
        progress.counts.lineItems = 0;
      }

      await publishProgress({
        phase: 'orders',
        resource: 'orders',
        fetched: progress.counts.orders ?? 0,
        currentPage: ordersResume.initialPage ?? 0,
        message: ordersResume.initialUrl
          ? `Resuming orders from page ${(ordersResume.initialPage ?? 0) + 1}...`
          : 'Fetching orders...',
      });

      await paginateShopifyResource<ShopifyOrder>(
        connection,
        'orders.json',
        { status: 'any' },
        async (orders, page, nextPageUrl) => {
          const orderRecords = orders.map((order) =>
            toSourceRecord('order', order.id, mapShopifyOrder(order), order as unknown as Record<string, unknown>, {
              sourceUpdatedAt: order.updated_at,
            }),
          );
          const lineItems = orders.flatMap((order) =>
            (order.line_items ?? []).map((item) => ({ raw: item, data: mapShopifyLineItem(item, order.id) })),
          );
          const lineItemRecords = lineItems.map(({ raw, data }) =>
            toSourceRecord('order_line_item', data.shopify_line_item_id, data, raw as unknown as Record<string, unknown>),
          );

          await ingestRecords([...orderRecords, ...lineItemRecords], setRetryState, signal, {
            phase: 'orders',
            shop: connection.shop_domain,
          });

          progress.counts.orders = (progress.counts.orders ?? 0) + orders.length;
          progress.counts.lineItems = (progress.counts.lineItems ?? 0) + lineItems.length;

          saveCheckpoint(progress, 'orders', {
            nextPageUrl,
            currentPage: page,
            fetched: progress.counts.orders,
            done: nextPageUrl === null,
          });

          await publishProgress({
            phase: 'orders',
            resource: 'orders',
            fetched: progress.counts.orders,
            currentPage: page,
            message: `Imported ${progress.counts.orders} orders across ${page} page${page === 1 ? '' : 's'}`,
          });
        },
        setRetryState,
        signal,
        ordersResume,
      );
      markPhaseComplete(progress, 'orders');
      await logPhase(
        'orders',
        errors.length > 0 ? 'failed' : 'success',
        `Orders: ${progress.counts.orders ?? 0}, Line items: ${progress.counts.lineItems ?? 0}`,
      );
    }

    if (shouldRunSyncResource(scope, 'customers') && (!persistProgress || !isPhaseComplete(progress, 'customers'))) {
      const customersResume = getPaginationResumeOptions(progress, 'customers');
      if (!customersResume.initialUrl) {
        progress.counts.customers = 0;
      }

      await publishProgress({
        phase: 'customers',
        resource: 'customers',
        fetched: progress.counts.customers ?? 0,
        currentPage: customersResume.initialPage ?? 0,
        message: customersResume.initialUrl
          ? `Resuming customers from page ${(customersResume.initialPage ?? 0) + 1}...`
          : 'Fetching customers...',
      });

      await paginateShopifyResource<ShopifyCustomer>(
        connection,
        'customers.json',
        undefined,
        async (customers, page, nextPageUrl) => {
          await ingestRecords(
            customers.map((customer) =>
              toSourceRecord('customer', customer.id, mapShopifyCustomer(customer), customer as unknown as Record<string, unknown>, {
                sourceUpdatedAt: customer.updated_at,
              }),
            ),
            setRetryState,
            signal,
            {
              phase: 'customers',
              shop: connection.shop_domain,
            },
          );

          progress.counts.customers = (progress.counts.customers ?? 0) + customers.length;

          saveCheckpoint(progress, 'customers', {
            nextPageUrl,
            currentPage: page,
            fetched: progress.counts.customers,
            done: nextPageUrl === null,
          });

          await publishProgress({
            phase: 'customers',
            resource: 'customers',
            fetched: progress.counts.customers,
            currentPage: page,
            message: `Imported ${progress.counts.customers} customers across ${page} page${page === 1 ? '' : 's'}`,
          });
        },
        setRetryState,
        signal,
        customersResume,
      );
      markPhaseComplete(progress, 'customers');
    }

    let locations: ShopifyLocation[] = [];
    if (shouldRunSyncResource(scope, 'locations')) {
      await publishProgress({
        phase: 'locations',
        resource: 'locations',
        fetched: 0,
        currentPage: 1,
        message: 'Fetching locations...',
      });

      const { data: locationPayload } = await withRetry(
        'shopify:locations',
        () => shopifyRequestJson<{ locations?: ShopifyLocation[] }>(
          buildShopifyAdminUrl(connection.shop_domain, 'locations.json'),
          connection.access_token,
          signal,
        ),
        {
          signal,
          maxAttempts: SHOPIFY_MAX_RETRIES,
          shouldRetry: shouldRetryShopify,
          onRetry: setRetryState,
        },
      );

      locations = locationPayload.locations ?? [];
      await ingestRecords(
        locations.map((location) =>
          toSourceRecord('location', location.id, mapShopifyLocation(location), location as unknown as Record<string, unknown>, {
            sourceUpdatedAt: location.updated_at,
          }),
        ),
        setRetryState,
        signal,
        {
          phase: 'locations',
          shop: connection.shop_domain,
        },
      );

      progress.counts.locations = locations.length;
      await publishProgress({
        phase: 'locations',
        resource: 'locations',
        fetched: locations.length,
        total: locations.length,
        message: `Imported ${locations.length} location${locations.length === 1 ? '' : 's'}`,
      });
      await logPhase(
        scope === 'full' ? 'customers_locations' : 'locations',
        errors.length > 0 ? 'failed' : 'success',
        `Customers: ${progress.counts.customers ?? 0}, Locations: ${progress.counts.locations ?? 0}`,
      );
    }

    if (shouldRunSyncResource(scope, 'inventory') && (!persistProgress || !isPhaseComplete(progress, 'inventory'))) {
      const hasInventoryCheckpoint = Object.entries(progress.checkpoints).some(
        ([key, checkpoint]) => key.startsWith('inventory:') && !checkpoint.done && !!checkpoint.nextPageUrl,
      );
      if (!hasInventoryCheckpoint) {
        progress.counts.inventoryLevels = 0;
      }

      await publishProgress({
        phase: 'inventory',
        resource: 'inventory',
        fetched: progress.counts.inventoryLevels ?? 0,
        total: null,
        currentPage: 0,
        currentLocationId: null,
        message: hasInventoryCheckpoint ? 'Resuming inventory levels...' : 'Fetching inventory levels...',
      });

      let inventoryLevelsCount = progress.counts.inventoryLevels ?? 0;
      for (const location of locations.filter((item) => item.active)) {
        const checkpointKey = `inventory:${location.id}`;
        if (getCheckpoint(progress, checkpointKey)?.done) {
          continue;
        }

        const inventoryResume = getPaginationResumeOptions(progress, checkpointKey);
        await paginateShopifyResource<ShopifyInventoryLevel>(
          connection,
          'inventory_levels.json',
          { location_ids: String(location.id) },
          async (levels, page, nextPageUrl) => {
            await ingestRecords(
              levels.map((level) =>
                toSourceRecord(
                  'inventory_level',
                  `${level.inventory_item_id}:${level.location_id}`,
                  mapShopifyInventoryLevel(level),
                  level as unknown as Record<string, unknown>,
                  { sourceUpdatedAt: level.updated_at },
                ),
              ),
              setRetryState,
              signal,
              {
                phase: 'inventory',
                shop: connection.shop_domain,
                locationId: location.id,
              },
            );

            inventoryLevelsCount += levels.length;
            progress.counts.inventoryLevels = inventoryLevelsCount;
            saveCheckpoint(progress, checkpointKey, {
              nextPageUrl,
              currentPage: page,
              fetched: inventoryLevelsCount,
              done: nextPageUrl === null,
            });

            await publishProgress({
              phase: 'inventory',
              resource: 'inventory',
              fetched: inventoryLevelsCount,
              currentPage: page,
              currentLocationId: location.id,
              message: `Imported ${inventoryLevelsCount} inventory levels. Current location: ${location.name}`,
            });
          },
          setRetryState,
          signal,
          inventoryResume,
        );
      }
      markPhaseComplete(progress, 'inventory');
      await logPhase(
        'inventory',
        errors.length > 0 ? 'failed' : 'success',
        `Inventory levels: ${progress.counts.inventoryLevels ?? 0}`,
      );
    }

    await publishProgress({
      phase: 'finalizing',
      resource: 'system',
      currentLocationId: null,
      message: 'Refreshing dashboard stats and closing out sync...',
    });

    await withRetry('refresh dashboard stats', () => refreshDashboardStats(signal), {
      signal,
      maxAttempts: SUPABASE_MAX_RETRIES,
      shouldRetry: shouldRetrySupabase,
      onRetry: setRetryState,
    });

    const finishedAt = nowIso();
    await insertSyncEvent(
      {
        event_type: scope === 'full' ? 'full_sync' : 'targeted_sync',
        event_category: 'system',
        direction: 'inbound',
        title: `Browser ${scopeLabel} success`,
        description: `Products: ${progress.counts.products ?? 0}, Orders: ${progress.counts.orders ?? 0}, Customers: ${progress.counts.customers ?? 0}, Inventory Levels: ${progress.counts.inventoryLevels ?? 0}`,
        status: 'success',
        source: scope === 'full' ? 'full_sync' : scope,
        metadata: {
          browser: true,
          scope,
          counts: progress.counts,
          errors: [],
        },
      },
      signal,
    );

    if (persistProgress) {
      await clearConnectionProgress(finishedAt, signal);
    }
    await onProgress?.({
      ...progress,
      status: 'success',
      phase: 'complete',
      resource: 'system',
      message: `Browser ${scopeLabel} complete`,
      retrying: null,
      errors: [],
      counts: { ...progress.counts },
    });

    return {
      status: 'success',
      counts: progress.counts,
      errors: [],
    };
  } catch (error) {
    if (isBrowserSyncCancelled(error)) {
      await publishProgress({
        status: 'cancelled',
        retrying: null,
        message: 'Browser sync cancelled',
      }, undefined).catch((progressError) => {
        logException(progressError, { operation: 'browser_sync_cancel_progress' });
      });

      await insertSyncEvent({
        event_type: scope === 'full' ? 'full_sync' : 'targeted_sync',
        event_category: 'system',
        direction: 'inbound',
        title: `Browser ${scopeLabel} cancelled`,
        description: `The browser ${scopeLabel} was cancelled before completion.`,
        status: 'skipped',
        source: scope === 'full' ? 'full_sync' : scope,
        metadata: {
          browser: true,
          scope,
          counts: progress.counts,
          errors,
        },
      }).catch((evtError) => {
        logException(evtError, { operation: 'browser_sync_cancel_event' });
      });

      return {
        status: 'cancelled',
        counts: progress.counts,
        errors,
      };
    }

    const message = error instanceof Error ? error.message : String(error);
    recordError(message, error);

    await publishProgress({
      status: 'failed',
      retrying: null,
      message,
    }, undefined).catch((progressError) => {
      logException(progressError, { operation: 'browser_sync_failure_progress' });
    });

    await insertSyncEvent({
      event_type: scope === 'full' ? 'full_sync' : 'targeted_sync',
      event_category: 'system',
      direction: 'inbound',
      title: `Browser ${scopeLabel} failed`,
      description: message,
      status: 'failed',
      error_message: message,
      source: scope === 'full' ? 'full_sync' : scope,
      metadata: {
        browser: true,
        scope,
        counts: progress.counts,
        errors,
      },
    }).catch((evtError) => {
      logException(evtError, { operation: 'browser_sync_failure_event' });
    });

    return {
      status: 'failed',
      counts: progress.counts,
      errors,
    };
  }
}
